import React from 'react';

function PrivacyPolicy() {
  return (
    <div className="max-w-3xl mx-auto px-5 py-10 font-body leading-relaxed text-slate-900">
      <h1 className="font-heading text-3xl font-bold mb-2">Privacy Policy</h1>
      <p className="text-slate-500 mb-4">Last Updated: February 5, 2026</p>

      <div className="mb-8 p-4 bg-slate-100 rounded-xl text-sm">
        <p className="font-semibold text-slate-700">Resmaker.ai is owned and operated by Impulse Theory LLC, a California limited liability company.</p>
        <p className="mt-2 text-slate-600">Contact: support@impulsetheory.com</p>
      </div>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">1. Information We Collect</h2>
        <p><strong>Information You Provide:</strong></p>
        <ul className="pl-6 mt-2 list-disc space-y-1">
          <li>Account: Email address, password (hashed with bcrypt)</li>
          <li>Resume: Your resume text, work history, skills</li>
          <li>Applications: Job descriptions, company names, status</li>
          <li>Q&A: Your answers about job applications</li>
          <li>Payments: Processed securely by Stripe (we don't store your card)</li>
        </ul>
        <p className="mt-3"><strong>Automatically Collected:</strong></p>
        <ul className="pl-6 mt-2 list-disc space-y-1">
          <li>Usage data, IP address, authentication tokens (JWT)</li>
          <li>Browser type, device information</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">2. How We Use Your Information</h2>
        <ul className="pl-6 list-disc space-y-1">
          <li>Provide the Service (generate tailored resumes, store data)</li>
          <li>Security (detect fraud via rate limiting)</li>
          <li>Improve the Service and develop new features</li>
          <li>Process payments and manage credits</li>
          <li>Communicate service updates and security alerts</li>
        </ul>
        <p className="mt-4 text-sm text-slate-600">Your data is shared with third-party service providers as described below. By using the Service, you consent to such data sharing.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">3. How We Share Your Information</h2>
        <p>We may share your information with:</p>
        <ul className="pl-6 mt-2 list-disc space-y-1">
          <li><strong>Anthropic Claude API:</strong> Your resume and job descriptions are sent for AI tailoring via HTTPS. See <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">Anthropic's privacy policy</a></li>
          <li><strong>Stripe:</strong> Payment processing. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">Stripe's privacy policy</a></li>
          <li><strong>Railway:</strong> Hosting infrastructure</li>
          <li><strong>PostHog:</strong> Analytics (optional)</li>
          <li><strong>Legal Requirements:</strong> When required by law or to protect rights</li>
          <li><strong>Business Transfers:</strong> In connection with merger or acquisition</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">4. Data Storage and Security</h2>
        <p><strong>Security Measures:</strong></p>
        <ul className="pl-6 mt-2 list-disc space-y-1">
          <li>Passwords hashed with bcrypt (10 rounds)</li>
          <li>JWT tokens with 30-day expiration</li>
          <li>Rate limiting (auth: 10 per 15min, AI: 50 per min)</li>
          <li>HTTPS encryption in transit</li>
          <li>API keys in environment variables</li>
        </ul>
        <p className="mt-3"><strong>Data Retention:</strong> Data retained while your account is active and as required for legal compliance.</p>
        <p className="mt-3 text-sm text-slate-600"><strong>Limitations:</strong> No system is 100% secure. Data sent to third parties is subject to their security practices.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">5. Your Privacy Rights</h2>
        <p><strong>All Users:</strong></p>
        <ul className="pl-6 list-disc space-y-1">
          <li><strong>Access:</strong> View all your data in "My Data" page</li>
          <li><strong>Export:</strong> Download your profile as JSON anytime</li>
          <li><strong>Delete:</strong> Request account deletion via support</li>
          <li><strong>Correct:</strong> Edit your resume and application data</li>
        </ul>
      </section>

      <section className="mb-8 p-4 bg-blue-50 rounded-xl border border-blue-200">
        <h2 className="font-heading text-xl font-semibold mb-3 text-blue-900">California Residents (CCPA)</h2>
        <p className="text-sm text-blue-800">California residents may have rights under the CCPA. To exercise: Email support@impulsetheory.com with subject "CCPA Request"</p>
      </section>

      <section className="mb-8 p-4 bg-purple-50 rounded-xl border border-purple-200">
        <h2 className="font-heading text-xl font-semibold mb-3 text-purple-900">EU/EEA Residents (GDPR)</h2>
        <p className="text-sm text-purple-800">If you are in the EEA, you have rights under GDPR:</p>
        <ul className="pl-6 mt-2 list-disc space-y-1 text-sm text-purple-700">
          <li>Right of Access, Rectification, Erasure</li>
          <li>Right to Restrict Processing</li>
          <li>Right to Data Portability</li>
          <li>Right to Object</li>
          <li>Rights related to Automated Decision-Making</li>
        </ul>
        <p className="mt-3 text-sm text-purple-800">To exercise: Email support@impulsetheory.com with subject "GDPR Request"</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">6. AI and Data Processing</h2>
        <p>When you use AI features, your resume and job description are sent to Anthropic's Claude API via HTTPS. Claude analyzes content and returns tailored suggestions. Anthropic may process data according to their own policies.</p>
        <p className="mt-3"><strong>Your Control:</strong> You control what data is submitted. You can review all AI-generated content. AI features are optional.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">7. Cookies & Tracking</h2>
        <p><strong>Essential Cookies:</strong> Required for authentication (cannot be disabled while using the Service)</p>
        <p className="mt-2"><strong>Analytics (PostHog):</strong> You can opt out via the cookie consent banner</p>
        <p className="mt-2 text-sm text-amber-700 bg-amber-50 p-2 rounded-lg"><strong>Note:</strong> Application errors may be captured for diagnostic purposes regardless of your analytics preferences.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">8. Children's Privacy</h2>
        <p>The Service is not intended for users under 18. We do not knowingly collect data from children under 18.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">9. International Data Transfers</h2>
        <p>Your data may be processed in the United States and other countries. By using the Service, you consent to international data processing.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">10. Data Breach Notification</h2>
        <p>In the event of a security breach, we will comply with applicable breach notification laws.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">11. Changes to This Policy</h2>
        <p>We may update this Privacy Policy periodically. The "Last Updated" date reflects the most recent changes. It is your responsibility to review this policy periodically.</p>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-2xl font-semibold mb-4">12. Contact Us</h2>
        <p><strong>Impulse Theory LLC</strong></p>
        <p className="mt-2">Email: support@impulsetheory.com</p>
      </section>

      <div className="mt-12 p-4 bg-slate-100 rounded-xl text-sm text-slate-600">
        <p className="font-semibold text-slate-700">By using Resmaker.ai, you consent to the collection and use of information as described in this Privacy Policy.</p>
        <p className="mt-2">You acknowledge this agreement is with Impulse Theory LLC, a California limited liability company.</p>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
